import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Card} from 'components';
import {TRANSLATION_KEYS} from 'locale';

import type {ValidationFailureResult, VersionLabel} from './types';
import {preSessionFlowAnalyticsUtils} from './utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.PRE_SESSION_FLOW.VALIDATION_FAILURES;

type Props = {
    data?: ValidationFailureResult[];
    versions: VersionLabel[];
    isLoading: boolean;
};

type FieldLabel = {
    stepName: string;
    fieldLabel: string;
};

export const PreSessionFlowValidationFailures = ({data, versions, isLoading}: Props) => {
    const {t} = useTranslation();
    const {stepNames, fieldLabels} = useMemo(() => preSessionFlowAnalyticsUtils.buildVersionLabelMaps(versions), [versions]);

    const resolveLabel = useCallback((stepId: string, fieldId: string): FieldLabel => ({
        stepName: stepNames.get(stepId) || `Step ${stepId.slice(0, 6)}`,
        fieldLabel: fieldLabels.get(fieldId) || `Field ${fieldId.slice(0, 6)}`,
    }), [stepNames, fieldLabels]);

    if (isLoading) {
        return <Card><div className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.LOADING)}</div></Card>;
    }
    // Nothing to show is the common case (most form submissions pass validation) — render nothing
    // rather than an empty "no failures" card. This card only earns its space when there ARE
    // failures, since it's the only place that breaks down WHICH fields clients fail.
    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Card>
            <h3 className='tw-ts-h5-bold tw-mb-3'>{t(T_KEYS.TITLE)}</h3>
            <div className='tw-overflow-x-auto'>
                <table className='tw-w-full tw-text-sm'>
                    <thead>
                        <tr className='tw-text-default-dimmed tw-text-xs tw-text-left'>
                            <th className='tw-pb-2 tw-pr-3'>{t(T_KEYS.STEP)}</th>
                            <th className='tw-pb-2 tw-pr-3'>{t(T_KEYS.FIELD)}</th>
                            <th className='tw-pb-2 tw-text-right tw-whitespace-nowrap'>{t(T_KEYS.FAILURES)}</th>
                            <th className='tw-pb-2 tw-text-right tw-whitespace-nowrap'>{t(T_KEYS.AFFECTED_SESSIONS)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row) => {
                            const label = resolveLabel(row.stepId, row.fieldId);
                            return (
                                <tr
                                    key={`${row.stepId}-${row.fieldId}`}
                                    className='tw-border-t tw-border-default'
                                >
                                    <td className='tw-py-2 tw-pr-3'>{label.stepName}</td>
                                    <td className='tw-py-2 tw-pr-3 tw-text-default-dimmed'>{label.fieldLabel}</td>
                                    <td className='tw-py-2 tw-text-right tw-tabular-nums'>{row.totalFailures}</td>
                                    <td className='tw-py-2 tw-text-right tw-tabular-nums'>{row.affectedSessions}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
